import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { TeamCost } from '../types';
import { updateTeamCostConfirmationById } from '../api/updateTeamCostConfirmationById';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { getBeautifiedLocalDate, getBeautifiedLocalDateTime } from '@/core';
import { updateTeamCostConfirmationBulk } from '../api/updateTeamCostConfirmationBulk';

type TeamCostsModalProps = {
  isOpen: boolean;
  setIsOpen: (show: boolean) => void;
  costs: TeamCost[];
};

const TeamCostsModal = ({ isOpen, setIsOpen, costs }: TeamCostsModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { invalidateAuditorVisit } = useAuditorVisitDetailContext();

  const { mutate, isLoading } = useMutation({
    mutationFn: updateTeamCostConfirmationById,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully updated team cost.', {
          defaultValue: 'Successfully updated team cost.',
        }),
        { variant: 'success' }
      );
      invalidateAuditorVisit();
    },
  });

  const updateAllCost = useMutation({
    mutationFn: updateTeamCostConfirmationBulk,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully updated team cost.', {
          defaultValue: 'Successfully updated team cost.',
        }),
        { variant: 'success' }
      );
      invalidateAuditorVisit();
    },
  });

  const getProposalAmountLabel = (forAuditor?: boolean) => {
    if (forAuditor) {
      return t('Price', { defaultValue: 'Price' });
    }
    return t('Proposal amount', { defaultValue: 'Proposal amount' });
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t('Costs', { defaultValue: 'Costs' })}</Typography>
        <CloseIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={() => setIsOpen(false)} />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              px: '1rem',
              pr: '2rem',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={() => {
                updateAllCost.mutate({ teamCostIds: costs.map((item) => item.id) });
              }}
              variant="outlined"
            >
              Confirm all
            </Button>
          </Box>
          <List>
            {!!costs &&
              costs.map((cost) => (
                <ListItem key={cost.id} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.25rem' }}
                    secondaryTypographyProps={{ fontSize: '1rem' }}
                    primary={cost?.costList?.name || 'N/A'}
                    secondary={
                      <Box>
                        <Typography>
                          {cost.costList?.name === 'Prevoženi kilometri'
                            ? t('Relation', { defaultValue: 'Relation' })
                            : t('Note', { defaultValue: 'Note' })}
                          : {cost.note}
                        </Typography>
                        {!cost.costList?.isDaily &&
                          !cost.costList?.forAuditor &&
                          (cost.isConfirmedByLeader ? (
                            <>
                              <Typography>
                                {t('Approved', { defaultValue: 'Approved' })}:{' '}
                                {cost.approvedQ
                                  ? cost.approvedQ.toString().replace('.', ',')
                                  : 'N/A'}
                              </Typography>
                              <Typography>
                                {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                                {cost.proposedQ
                                  ? cost.proposedQ.toString().replace('.', ',')
                                  : 'N/A'}
                              </Typography>
                            </>
                          ) : (
                            <Typography>
                              {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                              {cost.proposedQ ? cost.proposedQ.toString().replace('.', ',') : 'N/A'}
                            </Typography>
                          ))}
                        {!cost.costList?.isDaily &&
                          cost.costList?.forAuditor &&
                          (cost.isConfirmedByLeader ? (
                            <>
                              <Typography>
                                {t('Approved', { defaultValue: 'Approved' })}:{' '}
                                {cost.approvedQ && cost.price
                                  ? cost.price.toString().replace('.', ',')
                                  : 'N/A'}
                              </Typography>
                              <Typography>
                                {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                                {cost.price ? cost.price.toString().replace('.', ',') : 'N/A'}
                              </Typography>
                            </>
                          ) : (
                            <Typography>
                              {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                              {cost.price ? cost.price.toString().replace('.', ',') : 'N/A'}
                            </Typography>
                          ))}
                        {cost.costList?.isDaily && (
                          <>
                            <Typography>
                              {t('Travel from', { defaultValue: 'Travel from' })}:{' '}
                              {cost.travelFrom
                                ? getBeautifiedLocalDateTime(cost.travelFrom)
                                : 'N/A'}
                            </Typography>
                            <Typography>
                              {t('Travel to', { defaultValue: 'Travel to' })}:{' '}
                              {cost.travelTo ? getBeautifiedLocalDateTime(cost.travelTo) : 'N/A'}
                            </Typography>
                            <Typography>
                              {t('Lunch', { defaultValue: 'Lunch' })}:{' '}
                              {cost.lunchFactor
                                ? t('Yes', { defaultValue: 'Yes' })
                                : t('No', { defaultValue: 'No' })}
                            </Typography>
                          </>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={cost.isConfirmedByLeader} />}
                    label={t('Is confirmed?', { defaultValue: 'Is confirmed?' })}
                    disabled={cost.isFeeConf || isLoading || updateAllCost.isLoading}
                    onChange={(e: any) =>
                      mutate({
                        teamCostId: cost.id,
                        data: { isConfirmedByLeader: e.target.checked },
                      })
                    }
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TeamCostsModal;
